/**
 * Frontend timezone utilities — ESM mirror of backend/utils/timezone.js helpers.
 * Zero dependencies; uses native Intl.DateTimeFormat.
 */

export const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Returns the UTC offset in minutes for a given IANA timezone at a given instant.
 * e.g. Sydney in January (AEDT) → 660
 */
export function getOffsetMinutes(tz, date = new Date()) {
  try {
    const d = date instanceof Date ? date : new Date(date);
    // Use formatToParts for reliable cross-engine tz math (toLocaleString
    // parsing is spec-undefined and fails on some JS engines).
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    });
    const parts = {};
    for (const p of fmt.formatToParts(d)) {
      if (p.type !== 'literal') parts[p.type] = p.value;
    }
    const h = parts.hour === '24' ? 0 : Number(parts.hour);
    const asUtcMs = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      h,
      Number(parts.minute),
      Number(parts.second)
    );
    return Math.round((asUtcMs - d.getTime()) / 60000);
  } catch {
    return 0;
  }
}

/**
 * Returns a human-readable timezone label.
 * e.g. 'Australia/Sydney' → 'Sydney (AEDT, UTC+11)'
 */
export function formatTzLabel(tz) {
  if (!tz) return '';
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(now);
    const abbr = parts.find((p) => p.type === 'timeZoneName')?.value || tz;
    const offsetMin = getOffsetMinutes(tz, now);
    const sign = offsetMin >= 0 ? '+' : '-';
    const absMin = Math.abs(offsetMin);
    const h = Math.floor(absMin / 60);
    const m = absMin % 60;
    const offset = m > 0
      ? `${sign}${h}:${String(m).padStart(2, '0')}`
      : `${sign}${h}`;
    const city = tz.split('/').pop().replace(/_/g, ' ');
    return `${city} (${abbr}, UTC${offset})`;
  } catch {
    return tz;
  }
}

/**
 * Format an instant (Date or ISO string) in the given IANA timezone.
 * @param {Date|string} instant
 * @param {string} tz  IANA tz string
 * @param {object} options  Intl.DateTimeFormat options (default: { dateStyle: 'medium' })
 */
export function formatInZone(instant, tz, options = { dateStyle: 'medium' }) {
  if (!instant) return '';
  try {
    const d = instant instanceof Date ? instant : new Date(instant);
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, { timeZone: tz, ...options }).format(d);
  } catch {
    return '';
  }
}

/**
 * Convert a wall-clock (date, time) in a given IANA timezone to the UTC instant.
 * Mirrors backend/utils/timezone.computeKickoffAt for preview/UI use — NOT
 * authoritative for writes (server recomputes + handles DST ambiguity).
 *
 * Uses a fixed-point refinement: guess UTC = Date.UTC(wall), then subtract
 * the tz offset at that guess. Converges in one iteration outside DST
 * transitions; inside a gap/ambiguity the value is approximate (server owns
 * the final answer + emits DST_GAP/DST_AMBIGUOUS warnings).
 */
export function zonedWallTimeToUtc(dateStr, timeStr, tz) {
  if (!dateStr || !timeStr || !tz) return null;
  const dm = DATE_ONLY_RE.exec(dateStr);
  const tm = /^(\d{2}):(\d{2})$/.exec(timeStr);
  if (!dm || !tm) return null;
  const y = Number(dm[1]), mo = Number(dm[2]), d = Number(dm[3]);
  const h = Number(tm[1]), mi = Number(tm[2]);
  const guess = Date.UTC(y, mo - 1, d, h, mi, 0);
  const off1 = getOffsetMinutes(tz, new Date(guess));
  const first = guess - off1 * 60000;
  const off2 = getOffsetMinutes(tz, new Date(first));
  const final = guess - off2 * 60000;
  return new Date(final);
}

/**
 * R225: Returns the YYYY-MM-DD string of a Date/ISO in the given IANA timezone.
 * The viewer-tz-aware replacement for `new Date(x).toISOString().slice(0,10)`.
 * @param {Date|string} instant
 * @param {string} tz IANA timezone; falls back to browser tz then 'UTC'
 */
export function formatDayInZone(instant, tz) {
  if (!instant) return '';
  const d = instant instanceof Date ? instant : new Date(instant);
  if (isNaN(d.getTime())) return '';
  const zone = tz || (typeof Intl !== 'undefined' && Intl.DateTimeFormat
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'UTC') || 'UTC';
  return formatDayInTz(d, zone);
}

/**
 * R225: Returns the YYYY-MM string for a Date/ISO in the given IANA timezone.
 * Viewer-tz-aware replacement for `${d.getUTCFullYear()}-${d.getUTCMonth()+1}`.
 */
export function formatMonthInZone(instant, tz) {
  const day = formatDayInZone(instant, tz);
  return day ? day.slice(0, 7) : '';
}

/**
 * R225: Today's YYYY-MM-DD in the viewer's (or supplied) timezone.
 * Replacement for `new Date().toISOString().slice(0,10)` in form defaults
 * and filename slugs where the user's local "today" is what matters.
 */
export function todayInZone(tz) {
  return formatDayInZone(new Date(), tz);
}

/**
 * Returns the YYYY-MM-DD string of a Date in the given IANA timezone.
 */
function formatDayInTz(d, tz) {
  try {
    // en-CA locale produces YYYY-MM-DD which is easy to compare/sort.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch {
    // Fallback: local date
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const dy = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${dy}`;
  }
}

/**
 * Returns the YYYY-MM-DD string for an event's day in the given IANA timezone.
 *
 * Rules (in priority order):
 *  1. If event.kickoffAt is present → format that UTC instant in tz.
 *  2. If event.matchDate (or event.date) is a bare date-only string matching
 *     DATE_ONLY_RE AND there is no kickoffAt → return it verbatim (avoids
 *     UTC-midnight off-by-one for legacy data).
 *  3. Otherwise parse event.matchDate || event.date as a Date and format in tz.
 */
export function eventDayInTz(event, tz) {
  const kickoff = event?.kickoffAt;
  if (kickoff) {
    const d = kickoff instanceof Date ? kickoff : new Date(kickoff);
    if (!isNaN(d.getTime())) return formatDayInTz(d, tz);
  }

  const dateStr = event?.matchDate || event?.date;
  if (typeof dateStr === 'string' && DATE_ONLY_RE.test(dateStr)) {
    // Trust verbatim — no UTC conversion for legacy all-day values.
    return dateStr;
  }

  if (!dateStr) return '';
  const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return formatDayInTz(d, tz);
}

/**
 * Format a match's kickoff time for display.
 *
 * Timezone preference: viewer's browser tz > match.matchTimezone > teamTz > 'UTC'
 * The viewer's local tz is always preferred so a Sydney-scheduled match shows
 * as e.g. 4:00 AM London for a London-based viewer (same UTC instant, viewer-
 * friendly wall time). 'UTC' is the universal fallback when no tz can be
 * resolved — it is never region-biased.
 * Instant preference:  match.kickoffAt > match.matchDate > match.date
 *
 * @param {object} match
 * @param {string} [teamTz]  Team's default IANA timezone (used only if browser tz unavailable)
 * @param {object} [options] Intl.DateTimeFormat options
 */
export function formatMatchWhen(match, teamTz, options) {
  // R225.1: viewer's browser tz is the source of truth. No region-hardcoded fallback.
  const browserTz = (typeof Intl !== 'undefined' && Intl.DateTimeFormat
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : '');
  const tz = browserTz || match?.matchTimezone || teamTz || 'UTC';
  // Prefer authoritative UTC instant.
  if (match?.kickoffAt) {
    return formatInZone(match.kickoffAt, tz, options || { dateStyle: 'medium', timeStyle: 'short' });
  }
  // Legacy / unmigrated: no kickoffAt. Avoid UTC-midnight off-by-one in
  // viewers west of UTC by NOT feeding bare date values through timezone
  // conversion. If we have a matchTime alongside a YYYY-MM-DD, compute the
  // zoned instant for display; else render the date part verbatim.
  const dateRaw = match?.matchDate || match?.date;
  if (!dateRaw) return '';
  let dateStr = null;
  if (typeof dateRaw === 'string') {
    if (DATE_ONLY_RE.test(dateRaw)) dateStr = dateRaw;
    else {
      // Handle ISO midnight-UTC legacy values "2026-03-08T00:00:00.000Z" by
      // extracting the date portion, not converting.
      const isoMidnightMatch = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.\d+)?Z?$/.exec(dateRaw);
      if (isoMidnightMatch) dateStr = isoMidnightMatch[1];
    }
  } else if (dateRaw instanceof Date && !isNaN(dateRaw.getTime())) {
    if (dateRaw.getUTCHours() === 0 && dateRaw.getUTCMinutes() === 0 && dateRaw.getUTCSeconds() === 0) {
      const y = dateRaw.getUTCFullYear();
      const mo = String(dateRaw.getUTCMonth() + 1).padStart(2, '0');
      const d = String(dateRaw.getUTCDate()).padStart(2, '0');
      dateStr = `${y}-${mo}-${d}`;
    }
  }
  if (dateStr && match?.matchTime) {
    const instant = zonedWallTimeToUtc(dateStr, match.matchTime, tz);
    if (instant) return formatInZone(instant, tz, options || { dateStyle: 'medium', timeStyle: 'short' });
  }
  if (dateStr) {
    // Date-only display: format the YYYY-MM-DD as a local date label without
    // tz conversion (would off-by-one west of UTC).
    try {
      const [y, mo, d] = dateStr.split('-').map(Number);
      return new Intl.DateTimeFormat(undefined, options || { dateStyle: 'medium' })
        .format(new Date(y, mo - 1, d, 12, 0, 0));
    } catch { return dateStr; }
  }
  // Fallback: real Date/ISO with non-midnight time — safe to format in tz.
  return formatInZone(dateRaw, tz, options || { dateStyle: 'medium', timeStyle: 'short' });
}
